<template>
    <div class="o-account-transactions">
        <div class="o-account-transactions__header">
            <h2>Account Transactions</h2>
            <p>This displays your pending transactions so you can easily pay your outstanding balances. Search all transactions by using the search functionality below.</p>
            <p>Any recent payments will be reflected in your account within 24 hours, Monday to Friday.</p>
        </div>

        <Loader v-if="loadingTransactions" />

        <div v-else-if="error" class="o-account-transactions__error">
            {{ error }}
        </div>

        <div v-else class="o-account-transactions__outstanding">
            <h3>Pending Transactions</h3>
            <div v-if="transactions.length" class="o-account-transactions__pending-transactions">
                
                <table class="c-transaction-table is-desktop">
                    <thead>
                        <tr>
                            <th>Select</th>
                            <th>Document Number</th>
                            <th>Date</th>
                            <th>Due Date</th>
                            <th>Amount inc. VAT</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="transaction in transactions" :key="transaction.id">
                            <td><input v-if="transaction.attributes.documentType == 'Invoice' || transaction.attributes.documentType == 'Credit Memo'" type="checkbox" @change="toggleInvoice(transaction, $event)" :checked="invoicesToPay.includes(transaction)"></td>
                            <td>{{ transaction.id }}</td>
                            <td>{{ formatDate(transaction.attributes.documentDate)}}</td>
                            <td>{{ transaction.attributes.documentType == 'Invoice' && transaction.attributes.dueDate ? formatDate(transaction.attributes.dueDate) : 'N/A'}}</td>
                            <td>£{{ parseFloat(transaction.attributes.outstandingValue).toFixed(2) }}</td>
                            <td>
                                <div v-if="transaction.attributes.documentType == 'Invoice'" @click="showPayInvoiceModal = true; invoicesToPay = [transaction]" class="c-pending-transaction__primary-button">Pay invoice</div>
                                <div v-if="transaction.attributes.documentType == 'Invoice' || transaction.attributes.documentType == 'Credit Memo'" @click="downloadInvoice(transaction, transaction.attributes.documentType)" class="c-pending-transaction__primary-button">Download invoice</div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table class="c-transaction-table is-mobile" v-for="transaction in transactions" :key="transaction.id">
                    <tbody>
                        <tr>
                            <td>Select Invoice</td>
                            <td><input v-if="transaction.attributes.documentType == 'Invoice' || transaction.attributes.documentType == 'Credit Memo'" type="checkbox" @change="toggleInvoice(transaction, $event)" :checked="invoicesToPay.includes(transaction)"></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Document Number</td>
                            <td>{{ transaction.id }}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Date</td>
                            <td>{{ formatDate(transaction.attributes.documentDate)}}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Due Date</td>
                            <td>{{ transaction.attributes.documentType == 'Invoice' && transaction.attributes.dueDate ? formatDate(transaction.attributes.dueDate) : 'N/A'}}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Amount inc. VAT</td>
                            <td>£{{ parseFloat(transaction.attributes.outstandingValue).toFixed(2) }}</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>Action</td>
                            <td>
                                <div v-if="transaction.attributes.documentType == 'Invoice'" @click="showPayInvoiceModal = true; invoicesToPay = [transaction]" class="c-pending-transaction__primary-button">Pay invoice</div>
                                <div v-if="transaction.attributes.documentType == 'Invoice' || transaction.attributes.documentType == 'Credit Memo'" @click="downloadInvoice(transaction, transaction.attributes.documentType)" class="c-pending-transaction__primary-button">Download invoice</div>
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
                <div class="o-account-transactions__actions">
                    <div @click="exportPendingTransactionsPdf" class="o-account-transactions__export">
                        Export statement
                    </div>
                    <div v-if="invoicesToPay.length && invoicesToPay.filter(invoice => invoice.attributes.documentType == 'Invoice').length" @click="showPayInvoiceModal = true" class="o-account-transactions__pay-all">
                        Pay selected invoices
                    </div>
                </div>
            </div>
    
            <p v-else>You don't have any outstanding transactions.</p>
        </div>

        <div v-if="showPayInvoiceModal" class="c-modal">
            <div class="c-modal__overlay"></div>
            <div class="c-modal__content">
                <h3>Would you like to pay {{ invoicesToPay.length > 1 ? 'these invoices?' : 'this invoice?' }}</h3>
                <div class="c-payment-summary">
                    <table class="c-payment-summary__items">
                        <thead>
                            <tr>
                                <th>Invoice numbers</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invoice in invoicesToPay" :key="invoice.id">
                                <td>{{ invoice.id }}</td>
                                <td>£{{ parseFloat(invoice.attributes.outstandingValue).toFixed(2) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="c-payment-summary__total">
                        <div class="c-payment-summary__total-title">
                            total
                        </div>  <div class="c-payment-summary__total-value">
                            £{{ parseFloat(invoicesToPay.reduce((total, invoice) => total + invoice.attributes.outstandingValue, 0)).toFixed(2) }}
                        </div>
                    </div>
                </div>
                <div class="c-modal__billing-address-select">
                    <div class="c-modal__billing-address-select-title">Select a billing address</div>
                    <select v-model="selectedBillingAddress" class="c-modal__billing-address-select-select" v-if="customerAddresses.length">
                        <option value="" disabled selected>Select a billing address</option>
                        <option v-for="address in customerAddresses" :key="address.entity_id" :value="address">{{ address.street }}, {{ address.city }}, {{ address.postcode }}</option>
                    </select>
                </div>
                <div v-if="modalError" class="c-modal__error">Please select a billing address</div>
                <div class="c-modal__buttons">
                    <div @click="payInvoices" class="c-modal__primary-button">Pay invoice</div>
                    <div class="c-modal__cancel-button" @click="showPayInvoiceModal = false; invoicesToPay = []">Cancel</div>
                </div>
            </div>
        </div>

        <div v-if="showPaymentSuccessModal" class="c-modal t-white">
            <div class="c-modal__overlay"></div>
            <div class="c-modal__content">
                <h3>Thank you for your payment</h3>
                <p><b>Thanks for your payment - your transactions will be updated within 24hrs</b></p><br /><br />
                <p><b>Transaction Date:</b> {{ formatDate(completedPayment.payment_completed) }}</p>
                <p><b>Transaction ID:</b> {{ completedPayment.transaction_id }}</p>
                <p><b>Account Number:</b> {{ accountNumber }}</p>
                <p><b>Billed To:</b> {{ paymentBillingAddress }}</p>
                <div class="c-payment-summary">
                    <table class="c-payment-summary__items">
                        <thead>
                            <tr>
                                <th>Invoice numbers</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invoice in JSON.parse(completedPayment.payment_items)" :key="invoice.id">
                                <td>{{ invoice.invoice_code }}</td>
                                <td>£{{ invoice.amount}}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="c-payment-summary__total">
                        <div class="c-payment-summary__total-title">
                            total
                        </div>  <div class="c-payment-summary__total-value">
                            £{{ parseFloat(JSON.parse(completedPayment.payment_items).reduce((total, invoice) => total + invoice.amount, 0)).toFixed(2) }}
                        </div>
                    </div>
                </div>
                <div class="c-modal__buttons">
                    <div @click="showPaymentSuccessModal = false" class="c-modal__primary-button">Back to transactions</div>
                </div>
            </div>
        </div>


        <!-- <div v-if="showInvoiceModal" class="o-account-transactions__invoice-modal">
            <div class="o-account-transactions__invoice-modal-overlay"></div>
            <div v-if="loadingInvoice" class="o-account-transactions__invoice-modal-content">
                loading invoice...
            </div>
            <div v-else class="o-account-transactions__invoice-modal-content">
                <h3>Invoice Details</h3>
                <p>Invoice ID: {{ invoiceToShow.id }}</p>
                <p>Type: {{ invoiceToShow.type }}</p>
                <div v-for="(value, index) of Object.entries(invoiceToShow.attributes)">
                    <p v-if="value[0] !== 'details'">{{ value[0] }}: {{ value[1] }}</p>
                    <div v-else>
                        <p>Details:</p>
                        <ul>
                            <li v-for="(detail, index) in value[1]">{{ detail }}></li>
                        </ul>
                    </div>
                </div>
                <button @click="showInvoiceModal = false; invoiceToShow = null">Close</button>
            </div>
        </div> -->
    </div>
</template>

<script>
    import InvoiceStatement from '../inc/pdf-generators/InvoiceStatement';
    import CreditMemoStatement from '../inc/pdf-generators/CreditMemoStatement';
    import PendingTransactionsStatement from '../inc/pdf-generators/PendingTransactionsStatement';
    import Loader from './Loader.vue';

    export default {
        name: 'OutstandingTransactionList',
        props: {
            accountNumber: {
                type: String,
                required: true
            },
            customerFirstName: {
                type: String,
                required: true
            },
            customerLastName: {
                type: String,
                required: true
            },
            customerEmail: {
                type: String,
                required: true
            },
            customerAddresses: {
                type: String,
                required: true
            },
            phoneNumber: {
                type: String,
                required: true
            },
            defaultBillingAddress: {
                type: Object,
                required: true
            }
        },
        components: {
            Loader
        },
        data() {
            return {
                transactions: [],
                error: null,
                loadingTransactions: false,
                loadingInvoice: false,
                showInvoiceModal: false,
                showPayInvoiceModal: false,
                showPaymentSuccessModal: false,
                modalError: false,
                invoiceToShow: null,
                invoicesToPay: [],
                selectedBillingAddress: null,
                insertedRecord: null,
                successfulTransactionID: null,
                completedPayment: null
            };
        },
        computed: {
            paymentBillingAddress() {
                const address = this.customerAddresses.find(address => address.entity_id == this.completedPayment.customer_address_entity_id);
                return `${address.street}, ${address.city}, ${address.postcode}`;
            }
        },
        methods: {
            formatDate(dateString) {
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
                const year = date.getFullYear();
                
                return `${day}/${month}/${year}`;
            },
            toggleInvoice(transaction, event) {
                if (event.target.checked) {
                    this.invoicesToPay.push(transaction);
                } else {
                    this.invoicesToPay = this.invoicesToPay.filter(inv => inv.id !== transaction.id);
                }
            },
            async fetchTransactions() {

                this.loadingTransactions = true;

                try {
                    const response = await fetch(
                        `/rest/V1/swallowdental/account-statement`,
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }

                    const data = await response.json();
                    this.loadingTransactions = false;
                    // Order the transactions in descending date order
                    data[0].sort((a, b) => new Date(b.attributes.documentDate) - new Date(a.attributes.documentDate));
                    this.transactions = data[0]; 

                } catch (error) {
                    this.loadingTransactions = false;
                    console.error("Error fetching transactions:", error);
                }
            },
            async getInvoice(invoiceID)
            {

                this.loadingInvoice = true;
                this.showInvoiceModal = true;

                try {
                    const response = await fetch(
                        `/rest/V1/swallowdental/invoice?type=invoice&search=${invoiceID}`,
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }

                    const data = await response.json();
                    return data[0][0];

                } catch (error) {
                    this.loadingInvoice = false;
                    this.error = "Something went wrong while fetching your account transactions.";
                    console.error("Error fetching transactions:", error);
                }
            },
            async createPaymentRecord()
            {
                try {
                    const response = await fetch(
                        `/rest/V1/swallowdental/payments`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                total: parseFloat(this.invoicesToPay.reduce((total, invoice) => total + invoice.attributes.outstandingValue, 0)).toFixed(2),
                                payment_items: this.invoicesToPay.map(invoice => {
                                    return {
                                        invoice_code: invoice.id,
                                        amount: invoice.attributes.outstandingValue
                                    }
                                }),
                                customer_address_entity_id: this.selectedBillingAddress.entity_id
                            })
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }

                    const data = JSON.parse(await response.json());

                    this.insertedRecord = data;

                } catch (error) {
                    console.error("Error making payment:", error);
                }
            },
            async initPayment()
            {
                try {
                    const response = await fetch(
                        `/rest/V1/swallowdental/payments/init`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                booking: {
                                    description: "Payment for invoice(s)",
                                    first_name: this.customerFirstName,
                                    last_name: this.customerLastName,
                                    email: this.customerEmail,
                                    line_1: this.selectedBillingAddress.street,
                                    country: this.selectedBillingAddress.country_id,
                                    city: this.selectedBillingAddress.city,
                                    postcode: this.selectedBillingAddress.postcode,
                                },
                                return_url: `${window.location.origin}/account-transactions`,
                                amount: parseFloat(this.invoicesToPay.reduce((total, invoice) => total + invoice.attributes.outstandingValue, 0)).toFixed(2),
                            })
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }

                    const data = JSON.parse(await response.json()) ;


                    if(data.paymentUrl) {
                        window.location.href = data.paymentUrl
                    }

                } catch (error) {
                    console.error("Error making payment:", error);
                }
            },
            async tryCompletePayment(transaction, vendorTxCode)
            {
                try {
                    const response = await fetch(
                        "/rest/V1/swallowdental/payments/complete",
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                transaction_id: transaction,
                                vendor_tx_code: vendorTxCode
                            })
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }

                    const data = JSON.parse(await response.json());
                    return data;

                } catch (error) {
                    console.error("Error completing payment:", error);
                }
            },
            async payInvoices() {

                if(!this.selectedBillingAddress){
                    this.modalError = true;
                    return;
                }

                this.modalError = false;

                await this.createPaymentRecord();
                await this.initPayment();
            },
            async exportPendingTransactionsPdf() {
                const customerData = {
                    firstName: this.customerFirstName,
                    lastName: this.customerLastName,
                    email: this.customerEmail,
                    phoneNumber: this.phoneNumber,
                    transactions: this.transactions,
                    accountNumber: this.accountNumber,
                    defaultBillingAddress: this.defaultBillingAddress
                };
                const generator = new PendingTransactionsStatement(customerData);
                await generator.loadLogoFromUrl('/media/logo/sd-pdf-header.png');
                await generator.generate();
            },
            async downloadInvoice(invoice, type) {
                const customerData = {
                    firstName: this.customerFirstName,
                    lastName: this.customerLastName,
                    email: this.customerEmail,
                    phoneNumber: this.phoneNumber,
                    transactions: this.transactions,
                    accountNumber: this.accountNumber,
                    defaultBillingAddress: this.defaultBillingAddress
                };
                const invoiceData = await this.getInvoice(invoice.id);
                if(type == 'Invoice'){
                    const statement = new InvoiceStatement(customerData, {...invoiceData, ...{
                        dueDate: invoice.attributes.dueDate,
                        reference: invoice.attributes.reference
                    }});
                    await statement.loadLogoFromUrl('/media/logo/sd-invoice-header.png');
                    await statement.generate();
                }
                else if(type == 'Credit Memo'){
                    const statement = new CreditMemoStatement(customerData, {...invoiceData, ...{
                        dueDate: invoice.attributes.dueDate,
                        reference: invoice.attributes.reference
                    }});
                    await statement.loadLogoFromUrl('/media/logo/sd-credit-memo-header.png');
                    await statement.generate();
                }
            }
        },
        async mounted() {
            this.fetchTransactions();
            
            const urlParams = new URLSearchParams(window.location.search);
            const transactionId = urlParams.get("transactionId");
            const vendorTxCode = urlParams.get("vendorTxCode");

            if (transactionId && vendorTxCode) {
                const completedPayment = await this.tryCompletePayment(transactionId, vendorTxCode);
                if(completedPayment) {
                    this.completedPayment = completedPayment;
                    this.showPaymentSuccessModal = true;
                }
            }
        }
    };

</script>